<template>
  <Teleport to="body">
    <Transition name="dialog-fade">
      <div v-if="modelValue" class="confirm-dialog-overlay" @click="handleCancel">
        <div class="confirm-dialog" @click.stop>
          <div class="confirm-dialog-header">
            <i class="bi bi-question-circle-fill"></i>
            <h4>{{ title || $t('common.confirm_dialog.default_title') }}</h4>
          </div>
          <div class="confirm-dialog-body">
            {{ message }}
          </div>
          <div class="confirm-dialog-footer">
            <button class="btn-confirm" @click="handleConfirm">
              {{ confirmText || $t('common.confirm_dialog.ok') }}
            </button>
            <button class="btn-cancel" @click="handleCancel">
              {{ cancelText || $t('common.confirm_dialog.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    default: ''
  },
  message: {
    type: String,
    required: true
  },
  confirmText: {
    type: String,
    default: ''
  },
  cancelText: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const handleConfirm = () => {
  emit('update:modelValue', false);
  emit('confirm');
};

const handleCancel = () => {
  emit('update:modelValue', false);
  emit('cancel');
};
</script>

<style lang="scss" scoped>
.confirm-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
}

.confirm-dialog {
  background: #FFFFFF;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  max-width: 400px;
  width: 90%;
  overflow: hidden;
}

.confirm-dialog-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid #E5E7EB;

  i {
    font-size: 24px;
    color: #F59E0B;
  }

  h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #222222;
  }
}

.confirm-dialog-body {
  padding: 24px;
  font-size: 14px;
  color: #6B7280;
  line-height: 1.6;
}

.confirm-dialog-footer {
  padding: 16px 24px;
  border-top: 1px solid #E5E7EB;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  button {
    padding: 8px 24px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;

    &:active {
      transform: scale(0.98);
    }
  }

  .btn-cancel {
    background: #FFFFFF;
    color: #6B7280;
    border: 1px solid #D1D5DB;

    &:hover {
      background-color: #F9FAFB;
      border-color: #9CA3AF;
    }
  }

  .btn-confirm {
    background: #306FC9;
    color: #FFFFFF;

    &:hover {
      background-color: #2563EB;
    }
  }
}

// Transition animations
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-fade-enter-active .confirm-dialog,
.dialog-fade-leave-active .confirm-dialog {
  transition: transform 0.2s ease;
}

.dialog-fade-enter-from .confirm-dialog,
.dialog-fade-leave-to .confirm-dialog {
  transform: scale(0.95);
}
</style>
